import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface SectionPerm {
  visible: boolean
  read_only: boolean
}

export interface TenantMember {
  id: string
  email: string
  role: string
  sections_access: Record<string, SectionPerm>
  invited_at: string
  accepted_at: string | null
  revoked_at: string | null
  expires_at: string
  status: 'pending' | 'active' | 'revoked' | 'expired'
}

export interface InviteResult {
  id: string
  email: string
  role: string
  invited_at: string
  expires_at: string
  invite_link: string | null
  email_sent: boolean
}

export function useMembers() {
  return useQuery<TenantMember[]>({
    queryKey: ['members'],
    queryFn: () => api.get<TenantMember[]>('/api/v1/members'),
    staleTime: 30 * 1000,
  })
}

export function useInviteMember() {
  const qc = useQueryClient()
  return useMutation<InviteResult, Error, { email: string; role: string }>({
    mutationFn: body => api.post<InviteResult>('/api/v1/members/invite', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  })
}

export function useRemoveMember() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: id => api.delete<void>(`/api/v1/members/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  })
}

export function useAcceptInvite() {
  return useMutation<{ is_owner: boolean; role: string | null; sections_access: Record<string, SectionPerm> }, Error, string>({
    mutationFn: token => api.post('/api/v1/members/accept', { token }),
  })
}
